import { useState } from "react";
import { UserCheck, Undo2, Loader2, Phone, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders } from "../lib/supabase";
import { useOnline } from "../lib/net";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/* Dispatcher view of the claim + manual release (no auto-release). */
export const ClaimSection = ({ dispatch: d, onChange }) => {
  const online = useOnline();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!("claimed_at" in d) || (!d.claimed_at && !d.claim_released_at && !d.completed_by_name)) return null;
  const open = ["sent", "viewed"].includes(d.status);
  const release = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/dispatches/${d.id}/release-claim`, { method: "POST", headers: await authHeaders() });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.detail || "release_failed");
      toast.success(`Claim released — anyone with the link can claim it now`);
      onChange({ claimed_at: null, claimed_by_name: null, claimed_by_phone: null, claim_released_at: b.claim_released_at, claim_release_count: b.claim_release_count });
      setConfirm(false);
    } catch (e) { toast.error(e.message === "not_releasable" ? "This job can't be released any more" : "Could not release — try again"); }
    setBusy(false);
  };
  return (
    <section className="card mt-5 fade-up" data-testid="claim-section">
      <p className="eyebrow mb-1 flex items-center gap-1"><UserCheck size={12} /> Claimed by</p>
      {d.claimed_at ? (
        <p className="text-sm" data-testid="claimed-by"><span className="notranslate font-bold" translate="no">{d.claimed_by_name}</span> · <span className="notranslate" translate="no">{fmt(d.claimed_at)}</span>
          {d.claimed_by_phone && <a href={`tel:${d.claimed_by_phone}`} className="ml-2 inline-flex items-center gap-1 text-primary notranslate" translate="no" data-testid="claimed-by-phone"><Phone size={12} /> {d.claimed_by_phone}</a>}
        </p>
      ) : (
        <p className="text-sm text-muted" data-testid="claim-none">{d.claim_released_at ? <>Released <span className="notranslate" translate="no">{fmt(d.claim_released_at)}</span> · not claimed yet</> : "Not claimed yet"}</p>
      )}
      {d.claim_release_count > 0 && <p className="text-xs text-muted mt-1" data-testid="claim-release-count">Released {d.claim_release_count}× by you</p>}
      {d.status === "completed" && <p className="text-sm mt-2" data-testid="completed-by-row">Completed by <span className="notranslate font-bold" translate="no">{d.completed_by_name || "—"}</span>{d.completed_by_phone && <> · <a href={`tel:${d.completed_by_phone}`} className="text-primary notranslate" translate="no">{d.completed_by_phone}</a></>}</p>}
      {d.claimed_at && open && (
        <button type="button" className="btn-secondary w-auto px-4 mt-3 flex items-center gap-2" onClick={() => setConfirm(true)} disabled={!online} title={online ? undefined : "Go online to release"} data-testid="release-claim-button">
          {online ? <Undo2 size={16} /> : <WifiOff size={16} />} {online ? "Release claim" : "Release claim (needs internet)"}
        </button>
      )}
      {confirm && (
        <div className="sheet-backdrop" onClick={() => setConfirm(false)} data-testid="release-confirm-backdrop">
          <div className="sheet" onClick={(e) => e.stopPropagation()} data-testid="release-confirm">
            <p className="font-bold text-lg">Release this claim?</p>
            <p className="text-sm text-muted mt-2"><span className="notranslate" translate="no">{d.claimed_by_name}</span> will no longer be able to complete this job. Anyone with the link can claim it.</p>
            <div className="flex gap-3 mt-4">
              <button type="button" className="btn-secondary" onClick={() => setConfirm(false)} disabled={busy} data-testid="release-cancel">Keep</button>
              <button type="button" className="btn-danger flex items-center justify-center gap-2" onClick={release} disabled={busy} data-testid="release-confirm-button">{busy ? <><Loader2 className="animate-spin" size={16} /> Releasing…</> : "Release"}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export const ClaimBadge = ({ dispatch: d }) => (d.claimed_at && d.status !== "completed" ? <span className="chip chip-blue text-[10px] notranslate" translate="no" data-testid="claim-badge" title={`Claimed by ${d.claimed_by_name}`}>Claimed</span> : null);
